/**
 * Performance Page - Search Performance Dashboard
 * 
 * Migrated from seo-engine-webtools-customer-module
 */

import { useState } from 'react';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Grid,
  Skeleton,
  ToggleButtonGroup,
  ToggleButton,
  Fade,
  Button,
} from '@mui/material';
import { ArrowBack as ArrowBackIcon } from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { usePerformanceData, useDailyAggregates } from '@/hooks/customer';
import { getLastNDays, formatCompactNumber, formatCTR, formatPosition } from '@/services/customer/formatters';
import { MetricCard, PerformanceChart, DataTable, InlineError, MetricCardSkeleton, ChartSkeleton, TableSkeleton } from '@/components/customer';
import type { Column } from '@/components/customer';

function CustomerPerformancePage() {
  const navigate = useNavigate();
  const [dateRange, setDateRange] = useState(28);
  const [tablePage, setTablePage] = useState(0);
  const [tablePageSize, setTablePageSize] = useState(50);
  
  const { startDate, endDate } = getLastNDays(dateRange);

  // Fetch aggregated data for chart (no pagination, grouped by date only)
  const { data: chartData, isLoading: chartLoading } = useDailyAggregates({
    startDate,
    endDate,
  });

  // Fetch paginated data for table
  const { data, isLoading, error, refetch } = usePerformanceData({
    startDate,
    endDate,
    dimensions: ['query', 'page', 'country', 'device'],
    page: tablePage + 1, // Backend uses 1-based indexing
    size: tablePageSize,
  });



  const handleDateRangeChange = (_event: React.MouseEvent<HTMLElement>, newRange: number | null) => {
    if (newRange !== null) {
      setDateRange(newRange);
      setTablePage(0); // Reset to first page when date range changes
    }
  };

  const handleTablePageChange = (_event: unknown, newPage: number) => {
    setTablePage(newPage);
  };

  const handleTablePageSizeChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setTablePageSize(parseInt(event.target.value, 10));
    setTablePage(0);
  };

  if (isLoading || chartLoading) {
    return (
      <Box>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
          <Box>
            <Skeleton variant="text" width={200} height={40} />
            <Skeleton variant="text" width={150} height={24} />
          </Box>
          <Skeleton variant="rectangular" width={350} height={32} />
        </Box>
        <Grid container spacing={3} sx={{ mb: 3 }}>
          {[1, 2, 3, 4].map((i) => (
            <Grid item xs={6} md={3} key={i}>
              <MetricCardSkeleton />
            </Grid>
          ))}
        </Grid>
        <ChartSkeleton height={300} variant="line" />
        <Box sx={{ mt: 3 }}>
          <TableSkeleton rows={10} columns={6} />
        </Box>
      </Box>
    );
  }

  // Transform rows for table display - ensure rows array exists
  const rows = data?.rows ?? [];
  const tableData = rows.map((row, index) => ({
    id: index,
    date: row.date || '-',
    query: row.dimensions.query || '-',
    page: row.dimensions.page || '-',
    device: row.dimensions.device || '-',
    country: row.dimensions.country || '-',
    searchAppearance: row.dimensions.searchAppearance || '-',
    clicks: row.metrics.clicks,
    impressions: row.metrics.impressions,
    ctr: row.metrics.ctr,
    position: row.metrics.avgPosition,
  }));

  const columns: Column<typeof tableData[0]>[] = [
    { field: 'date', headerName: 'Date', width: 110 },
    { field: 'query', headerName: 'Query', flex: 2 },
    { field: 'page', headerName: 'Page', flex: 2 },
    // { field: 'device', headerName: 'Device', width: 90, hide: true }, // Hidden column
    { field: 'country', headerName: 'Country', width: 80 },
    { field: 'clicks', headerName: 'Clicks', width: 90, align: 'right' },
    { field: 'impressions', headerName: 'Impressions', width: 110, align: 'right' },
    { field: 'ctr', headerName: 'CTR', width: 90, align: 'right', valueGetter: (row) => formatCTR(row.ctr) },
    { field: 'position', headerName: 'Position', width: 90, align: 'right', valueGetter: (row) => formatPosition(row.position) },
    // { field: 'searchAppearance', headerName: 'Appearance', width: 120, hide: true }, // Hidden column
  ];

  return (
    <Fade in timeout={300}>
      <Box sx={{ maxWidth: { xs: 400, sm: 768, md: 900, lg: 1200 }, mx: 'auto', px: { xs: 2, sm: 3, md: 4 }, py: 3 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
          <Box>
            <Typography variant="h4" gutterBottom>
              Search Performance
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {startDate} to {endDate}
            </Typography>
          </Box>

          <ToggleButtonGroup
            value={dateRange}
            exclusive
            onChange={handleDateRangeChange}
            size="small"
          >
            <ToggleButton value={7}>7 Days</ToggleButton>
            <ToggleButton value={14}>14 Days</ToggleButton>
            <ToggleButton value={28}>28 Days</ToggleButton>
            <ToggleButton value={90}>90 Days</ToggleButton>
          </ToggleButtonGroup>
        </Box>

        {/* Show error alert if data failed to load */}
        {error && (
          <InlineError error={error} title="Failed to load performance data" onRetry={refetch} />
        )}

        {/* Metric Cards */}
        <Grid container spacing={3} sx={{ mb: 3 }}>
          <Grid item xs={6} md={3}>
            <MetricCard
              title="Total Clicks"
              value={formatCompactNumber(chartData?.aggregates?.totalClicks ?? 0)}
              change={data?.comparison?.changes?.clicksChange}
              changeLabel="vs prev period"
              tooltip="Total number of clicks on your pages in search results."
            />
          </Grid>
          <Grid item xs={6} md={3}>
            <MetricCard
              title="Total Impressions"
              value={formatCompactNumber(chartData?.aggregates?.totalImpressions ?? 0)}
              change={data?.comparison?.changes?.impressionsChange}
              changeLabel="vs prev period"
              tooltip="Number of times your pages appeared in search results."
            />
          </Grid>
          <Grid item xs={6} md={3}>
            <MetricCard
              title="Average CTR"
              value={formatCTR(chartData?.aggregates?.avgCtr ?? 0)}
              change={data?.comparison?.changes?.ctrChange}
              changeLabel="vs prev period"
              tooltip="Click-through rate: clicks divided by impressions."
            />
          </Grid>
          <Grid item xs={6} md={3}>
            <MetricCard
              title="Average Position"
              value={formatPosition(chartData?.aggregates?.avgPosition ?? 0)}
              change={data?.comparison?.changes?.positionChange}
              changeLabel="vs prev period"
              invertChange
              tooltip="Average ranking position in search results. Lower is better."
            />
          </Grid>
        </Grid>

        {/* Performance Chart */}
        <Card sx={{ mb: 3 }}>
          <CardContent>
            <Typography variant="h6" gutterBottom>
              Performance Trend
            </Typography>
            <PerformanceChart data={chartData} />
          </CardContent>
        </Card>

        {/* Data Table */}
        <Card>
          <CardContent>
            <Typography variant="h6" gutterBottom>
              Top Queries & Pages
            </Typography>
            <Box>
              <DataTable data={tableData} columns={columns} pageSize={tablePageSize} />
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mt: 2, px: 2 }}>
                <Typography variant="body2" color="text.secondary">
                  Showing {tableData.length} of {data?.totals?.clicks || 0} results
                </Typography>
                <Box sx={{ display: 'flex', gap: 1 }}>
                  <Button
                    size="small"
                    disabled={tablePage === 0}
                    onClick={() => setTablePage(tablePage - 1)}
                  >
                    Previous
                  </Button>
                  <Typography variant="body2" sx={{ display: 'flex', alignItems: 'center', px: 2 }}>
                    Page {tablePage + 1}
                  </Typography>
                  <Button
                    size="small"
                    disabled={tableData.length < tablePageSize}
                    onClick={() => setTablePage(tablePage + 1)}
                  >
                    Next
                  </Button>
                </Box>
              </Box>
            </Box>
          </CardContent>
        </Card>
      </Box>
    </Fade>
  );
}

export default CustomerPerformancePage;
